import React, { useState, useEffect } from 'react';
import { Table, Button, Spinner, Alert, Container, Row, Col, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { storage } from '../../firebaseConfig';
import { ref, deleteObject } from 'firebase/storage';
import { FireStoreCategory } from '../../model/CategoryModel';
import { CategoryGateway } from '../../gateways/CategoryGateway';
import { Utils } from '../../common/Utils';

const CategoryList: React.FC = () => {
    const [categories, setCategories] = useState<FireStoreCategory[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [showModal, setShowModal] = useState<boolean>(false);
    const [selectedCategory, setSelectedCategory] = useState<FireStoreCategory | null>(null);

    const navigate = useNavigate();

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const categoryList = await CategoryGateway.GetCategories();
                categoryList.sort((a, b) => a.category.name.localeCompare(b.category.name));
                setCategories(categoryList);
            } catch (err) {
                setError('Error fetching categories.');
            } finally {
                setLoading(false);
            }
        };

        fetchCategories();
    }, []);

    const handleEdit = (id: string) => {
        navigate(Utils.GetUrl(`/admin/category/${id}`));
    };

    const handleDelete = async () => {
        if (!selectedCategory) return;

        try {
            await CategoryGateway.DeleteCategory(selectedCategory.id);

            if (selectedCategory.category.imageUrl) {
                const imageRef = ref(storage, selectedCategory.category.imageUrl);
                await deleteObject(imageRef);
            }

            setCategories(categories.filter(category => category.id !== selectedCategory.id));
            setShowModal(false);
            alert('Category deleted successfully!');
        } catch {
            setError('Error deleting category.');
        }
    };

    const confirmDelete = (category: FireStoreCategory) => {
        setSelectedCategory(category);
        setShowModal(true);
    };

    return (
        <Container className="main-container p-3">
            <Row className="align-items-center mb-3">
                <Col>
                    <h2>Category List</h2>
                </Col>
                <Col className="text-end">
                    <Button variant="primary" onClick={() => navigate(Utils.GetUrl('/admin/category'))}>Add Category</Button>
                </Col>
            </Row>

            {loading && <Spinner animation="border" />}
            {error && <Alert variant="danger">{error}</Alert>}
            <Table striped bordered hover>
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Description</th>
                        <th>Image</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {categories.map((firestoreCategory) => (
                        <tr key={firestoreCategory.id}>
                            <td>{firestoreCategory.category.name}</td>
                            <td>{firestoreCategory.category.description || 'N/A'}</td>
                            <td>
                                {firestoreCategory.category.imageUrl && <img src={firestoreCategory.category.imageUrl} alt={firestoreCategory.category.name} width="50" />}
                            </td>
                            <td>
                                <Button onClick={() => handleEdit(firestoreCategory.id)}>Edit</Button>{' '}
                                <Button variant="danger" onClick={() => confirmDelete(firestoreCategory)}>Delete</Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
            {/* Delete Confirmation Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete <strong>{selectedCategory?.category.name}</strong>? This action cannot be undone.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
                    <Button variant="danger" onClick={handleDelete}>Delete</Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default CategoryList;